import { api } from "../../../services/auth";
import { loyaltyFetch } from "../hooks/loyaltyFetch";
import {
    fetchLoyaltyCustomerRecord,
    resolveContactEmailForLoyalty,
} from "./loyaltyCustomerApi";
import { LOYALTY_API_BASE } from "../constants";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function looksLikeEmail(value) {
    const s = String(value || "").trim();
    return s.includes("@") && EMAIL_RE.test(s);
}

/** Email Unomi pour un profileId CDP — même logique que la fiche /contacts/. */
export async function resolveEmailFromUnomiProfile(profileId, brandName = "") {
    const id = String(profileId || "").trim();
    if (!id || looksLikeEmail(id)) return "";
    try {
        const profileRes = await api.get(`/profiles/${encodeURIComponent(id)}`);
        return resolveContactEmailForLoyalty(profileRes.data?.profile, brandName);
    } catch {
        return "";
    }
}

/** Ne jamais appeler GET /customers/{brand}/{id} avec un email dans le path. */
export function isDirectCustomerPathAllowed(identifier) {
    const s = String(identifier || "").trim();
    if (!s) return false;
    return !looksLikeEmail(s);
}

async function searchCustomersByQuery(brandName, query, limit = 20, offset = 0) {
    const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
    });
    const q = String(query || "").trim();
    if (q) params.set("q", q);
    if (looksLikeEmail(q)) params.set("email", q);

    const res = await loyaltyFetch(
        `${LOYALTY_API_BASE}/customers?${params}`,
        { headers: { "X-Brand": brandName } },
    );
    if (!res.ok) return { items: [], total: 0 };
    const data = await res.json().catch(() => ({}));
    return {
        items: Array.isArray(data?.items) ? data.items : [],
        total: Number(data?.count ?? data?.total ?? 0),
    };
}

/**
 * Recherche paginée admin (segments, etc.).
 * Email → réconciliation Loyalty ; fragment UUID → GET /customers?q=
 */
export async function searchLoyaltyCustomersPaginated(brandName, query, { limit = 20, offset = 0 } = {}) {
    const q = String(query || "").trim();
    if (!brandName) return { items: [], total: 0 };

    if (looksLikeEmail(q)) {
        try {
            const outcome = await lookupLoyaltyCustomer(brandName, q);
            if (outcome.kind === "single") {
                return { items: [outcome.customer], total: 1 };
            }
            return { items: outcome.items || [], total: outcome.items?.length || 0 };
        } catch {
            return { items: [], total: 0 };
        }
    }

    return searchCustomersByQuery(brandName, q, limit, offset);
}

/** Résout un email Unomi → profileId (itemId) pour handle d'entrée. */
export async function resolveUnomiProfileIdFromEmail(email) {
    const normalized = String(email || "").trim().toLowerCase();
    if (!normalized) return null;

    const res = await api.post("/profiles/search", {
        offset: 0,
        limit: 10,
        search_term: normalized,
    });
    const profiles = Array.isArray(res.data?.profiles) ? res.data.profiles : [];
    const exact = profiles.find(
        (p) => String(p?.properties?.email || "").trim().toLowerCase() === normalized,
    );
    return exact?.itemId || profiles[0]?.itemId || null;
}

export async function resolveRouteProfileId(routeId, email = "") {
    const raw = String(routeId || "").trim();
    if (!raw) return "";
    if (!looksLikeEmail(raw)) return raw;

    const profileId = await resolveUnomiProfileIdFromEmail(raw);
    if (!profileId && !email) {
        throw new Error("Aucun contact Unomi trouvé pour cet email. Utilisez le Profile ID (UUID).");
    }
    return profileId || raw;
}

/**
 * Recherche client fidélité (dialogue consultation).
 * Email → GET customer avec ?email= (réconciliation Loyalty, indépendant de la fusion Unomi).
 */
export async function lookupLoyaltyCustomer(brandName, query) {
    const q = String(query || "").trim();
    if (!brandName || !q) {
        throw new Error("Marque et identifiant requis.");
    }

    if (looksLikeEmail(q)) {
        const unomiHandle = await resolveUnomiProfileIdFromEmail(q);
        const entryProfileId = unomiHandle || "email-lookup";
        const result = await fetchLoyaltyCustomerRecord(brandName, entryProfileId, q);
        if (!result.ok || !result.customer) {
            throw new Error(
                "Aucun client fidélité trouvé pour cet email. Vérifiez la marque active ou attendez la première transaction.",
            );
        }
        return {
            kind: "single",
            customer: result.customer,
            resolvedProfileId: result.profileId,
        };
    }

    const matchesResult = await searchCustomersByQuery(brandName, q, 20, 0);
    const matches = matchesResult.items;
    if (matches.length === 0) {
        if (isDirectCustomerPathAllowed(q)) {
            let result = await fetchLoyaltyCustomerRecord(brandName, q);
            if (!result.ok || !result.customer) {
                try {
                    const profileRes = await api.get(`/profiles/${encodeURIComponent(q)}`);
                    const email = resolveContactEmailForLoyalty(profileRes.data?.profile, brandName);
                    if (email) {
                        result = await fetchLoyaltyCustomerRecord(brandName, q, email);
                    }
                } catch {
                    /* profil Unomi absent ou inaccessible */
                }
            }
            if (result.ok && result.customer) {
                return {
                    kind: "single",
                    customer: result.customer,
                    resolvedProfileId: result.profileId,
                };
            }
        }
        throw new Error(
            "Client introuvable pour cette marque. Essayez l'email du contact ou le Profile ID Loyalty actuel.",
        );
    }

    if (matches.length === 1) {
        return {
            kind: "single",
            customer: matches[0],
            resolvedProfileId: matches[0].profile_id,
        };
    }

    const exact = matches.find((row) => String(row.profile_id) === q);
    if (exact) {
        return { kind: "single", customer: exact, resolvedProfileId: exact.profile_id };
    }

    return { kind: "multiple", items: matches };
}
